import React, { useEffect, useState } from "react";
import { ArrowLeft, Trash2 } from "lucide-react";
import {
  GenericTable,
  RoleValidator,
  SearchableDropdown,
} from "../../../Components/Common";
import { useDispatchNote } from "../Hooks/useDispatchNote";
import { useMRR } from "../Hooks/useMRR";
import {
  DISPATCH_NOTE_MATERIAL_COLUMN,
  DISPATCH_NOTE_MATERIAL_LABEL,
  DISPATCH_NOTE_STATUS_LABEL,
  DISPATCH_NOTE_STATUS,
  getDNStatusColor,
  STOCK_MODAL_MODES,
} from "../constants";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  DETAIL_DISPATCH_NOTE,
  DISPATCH_NOTE,
  EDIT_DISPATCH_NOTE,
} from "../../../routes";
import { MdModeEditOutline } from "react-icons/md";
import { ROLES } from "../../../Constants/Strings";
import { getAvailableMaterialsByWarehouseAndMRR } from "../../../Services/materialRequestService";
import toast from "react-hot-toast";
import { extractErrorMessage } from "../../../Utils/extractErrorMessage";
import { TOAST_MESSAGES } from "../constants";

export default function DispatchNoteAction() {
  const {
    loading,
    fetchWarehouses,
    postDispatchNotes,
    fetchDispatchNotesById,
    updateDispatchNote,
  } = useDispatchNote();

  const { fetchAllMRRs, fetchMRRMaterial } = useMRR();

  const [mode, setMode] = useState(STOCK_MODAL_MODES.ADD);
  const [warehouse, setWarehouse] = useState("");
  const [dn_code, setDNCode] = useState("");
  const [warehouseId, setWarehouseId] = useState("");
  const [status, setStatus] = useState("");
  const [mrr, setMRR] = useState("");
  const [mrrId, setMRRId] = useState("");
  const location = useLocation();
  const [materials, setMaterials] = useState([]);
  const [errors, setErrors] = useState({});
  const navigate = useNavigate();
  const param = useParams();
  const [pageLoading, setPageLoading] = useState(true);

  const removeMaterial = (id) => {
    setMaterials((prev) => prev.filter((item) => item.id !== id));
  };

  const handleInputChange = (id, field, value) => {
    setMaterials((prevMaterials) =>
      prevMaterials.map((item) =>
        item.id === id ? { ...item, [field]: value } : item
      )
    );
  };

  const renderField = (column, row) => {
    switch (column) {
      case "product_name":
        return (
          <div className="flex flex-col">
            <span>{row.product_name}</span>
            <span className="text-gray-500 font-normal">
              {row.specification}
            </span>
            <span className="text-gray-800 font-normal italic">
              ({row.UOM})
            </span>
          </div>
        );
      case "expected_quantity":
        return row.expected_quantity || "N/A";
      case "dispatched_quantity":
        const isEditable = mode === STOCK_MODAL_MODES.ADD || mode === STOCK_MODAL_MODES.EDIT;

        const exceedsExpected = isEditable &&
          Number(row.dispatched_quantity || 0) > Number(row.expected_quantity || 0);
        const exceedsAvailable = isEditable &&
          Number(row.dispatched_quantity || 0) > Number(row.available_quantity || 0);

        return (
          <div className="flex flex-col">
            <input
              type="number"
              min="1"
              value={row.dispatched_quantity || ""}
              onChange={(e) => {
                const value = Number(e.target.value);
                handleInputChange(row.id, "dispatched_quantity", value);
              }}
              className={`w-24 text-center border rounded-lg px-2 py-1 focus:border-blue-500 transition-all duration-150 outline-none shadow-sm ${
                exceedsExpected || exceedsAvailable
                  ? "border-red-500"
                  : "border-gray-300"
              }`}
              placeholder="Qty"
              disabled={mode === STOCK_MODAL_MODES.VIEW}
            />
            {isEditable && exceedsAvailable && (
              <span className="text-red-500 text-xs mt-1">
                Quantity not available
              </span>
            )}
            {isEditable && exceedsExpected && !exceedsAvailable && (
              <span className="text-red-500 text-xs mt-1">
                Exceeds expected quantity
              </span>
            )}
          </div>
        );
      case "note":
        return (
          <input
            type="text"
            value={
              mode === STOCK_MODAL_MODES.VIEW
                ? row.note || "N/A"
                : row.note ?? ""
            }
            onChange={(e) => handleInputChange(row.id, "note", e.target.value)}
            placeholder="Enter note..."
            className="w-full border border-gray-300 rounded-lg px-3 py-1 
                      focus:ring-2 focus:ring-blue-500 focus:border-blue-500
                      transition-all duration-150 outline-none shadow-sm"
            disabled={mode === STOCK_MODAL_MODES.VIEW}
          />
        );
      case "actions":
        return (
          <>
            <RoleValidator allowedRoles={[ROLES.INVENTORY_INCHARGE]}>
              {mode !== STOCK_MODAL_MODES.VIEW && (
                <button
                  className="hover:underline ml-2"
                  onClick={() => removeMaterial(row.id)}
                >
                  <Trash2 className="h-5 w-5 text-red-400" />
                </button>
              )}
            </RoleValidator>
          </>
        );
      default:
        return row[column];
    }
  };

  const handleSubmit = async (status) => {
    if (!warehouseId || !mrrId) {
      alert("Please select a warehouse and MRR.");
      return;
    }
    if (materials.length === 0) {
      alert("Please add at least one material.");
      return;
    }
    
    const payload = { warehouse: warehouseId, mrr: mrrId, materials, status };
    var data = null;
    if (mode === STOCK_MODAL_MODES.ADD) {
      data = await postDispatchNotes(payload);
    } else {
      data = await updateDispatchNote(param.instanceId, payload);
      if (mode === STOCK_MODAL_MODES.VIEW) {
        getDispatchNoteData();
      }
    }
    navigate(DETAIL_DISPATCH_NOTE.replace(":instanceId", data.id));
  };

  const getDispatchNoteData = async () => {
    const data = await fetchDispatchNotesById(param.instanceId);
    setMaterials(data.materials);
    setMRR(data.mrr_code);
    setMRRId(data.mrr);
    setWarehouse(data.warehouse_name);
    setWarehouseId(data.warehouse);
    setDNCode(data.dn_code || "N/A");
    setStatus(data.status);
    setPageLoading(false);
  };

  useEffect(() => {
    if (param.instanceId) {
      getDispatchNoteData();

      if (location.pathname.includes("/edit")) {
        setMode(STOCK_MODAL_MODES.EDIT);
      } else if (location.pathname.includes("/view")) {
        setMode(STOCK_MODAL_MODES.VIEW);
      }
    } else {
      setMode(STOCK_MODAL_MODES.ADD);
      setPageLoading(false);
    }
  }, [param.instanceId, location.pathname]);

useEffect(() => {
  const fetchAvailableMaterials = async () => {
    if (!mrrId || !warehouseId || mode !== STOCK_MODAL_MODES.ADD) return;

    try {
      const response = await getAvailableMaterialsByWarehouseAndMRR(
        mrrId,
        warehouseId
      );
      if(response.status === 200){
        const updatedResponse = response.data.map((item) => ({
          ...item,
          expected_quantity: item.quantity,
          dispatched_quantity: 1,
          available_quantity: item.available_quantity,
        }));
        setMaterials(updatedResponse);
      }
    } catch (error) {
     toast.error(
          extractErrorMessage(error) || TOAST_MESSAGES.FAILED_FETCH_MATERIALS
        );
    }
  };

  fetchAvailableMaterials();
}, [mrrId, warehouseId, mode]);

  const hasError = (mode === STOCK_MODAL_MODES.ADD || mode === STOCK_MODAL_MODES.EDIT) && 
  materials.some((m) =>
      Number(m.dispatched_quantity || 0) > Number(m.expected_quantity || 0) ||
      Number(m.dispatched_quantity || 0) > Number(m.available_quantity || 0)
  );

  if (pageLoading) {
    return (
      <div className="flex items-center justify-center h-[70vh]">
        <div className="flex flex-col gap-3">
          <div className="w-10 h-10 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
          <p className="text-gray-600 font-medium">Loading Dispatch Note...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[70vh] p-2">
      <div className="flex justify-between items-center">
        <h2 className="flex gap-2 items-center text-xl font-semibold text-gray-800 m-4">
          <button
            type="button"
            onClick={() => navigate(DISPATCH_NOTE)}
            className="text-primary text-center text-sm font-lg"
          >
            <ArrowLeft size={25} absoluteStrokeWidth className="text-primary" />
          </button>
          {mode} Dispatch Note
        </h2>
        {mode === STOCK_MODAL_MODES.VIEW &&
          status === DISPATCH_NOTE_STATUS.DRAFT && (
            <RoleValidator allowedRoles={[ROLES.INVENTORY_INCHARGE]}>
              <button
                className="mb-4 flex gap-1 px-4 py-2 bg-primary text-white rounded hover:bg-primary transition"
                onClick={() =>
                  navigate(
                    EDIT_DISPATCH_NOTE.replace(":instanceId", param.instanceId)
                  )
                }
              >
                <MdModeEditOutline size={20} />
              </button>
            </RoleValidator>
          )}
      </div>
      <div className="mx-auto grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Left Side — Warehouse & Notes */}
        <div className="bg-white p-6 md:border-r-2">
          <h2 className="text-xl text-center font-semibold text-gray-800 mb-4">
            Dispatch Note
          </h2>

          {/* Dispatch Note Code Field */}
          {mode !== STOCK_MODAL_MODES.ADD && dn_code && (
            <div className="my-4 flex gap-4 flex-col">
              <div className="flex">
                <span> Dispatch Note Code :</span>
                <span className={`px-2 font-semibold rounded-lg`}>
                  {dn_code}
                </span>
              </div>
              <div className="flex">
                <span> Status :</span>
                <span
                  className={`mx-4 px-3 font-semibold rounded-lg ${getDNStatusColor(
                    status
                  )}`}
                >
                  {DISPATCH_NOTE_STATUS_LABEL[status]}
                </span>
              </div>
            </div>
          )}

          {/* Warehouse Dropdown */}
          <SearchableDropdown
            label="Source Warehouse"
            fetchFunction={fetchWarehouses}
            onSelect={(val) => setWarehouseId(val)}
            valueKey="id"
            labelKey="name"
            placeholder={warehouse ? warehouse : "Select warehouse"}
            disabled={mode !== STOCK_MODAL_MODES.ADD}
          />

          {/* MRR Field */}
          <div className="mt-4">
            <SearchableDropdown
              label="Select Material Requisition Request(MRR)"
              fetchFunction={(param) => fetchAllMRRs({ ...param })}
              onSelect={(val) => setMRRId(val)}
              valueKey="id"
              labelKey="mrr_code"
              placeholder={mrr ? mrr : "Select MRR"}
              disabled={mode !== STOCK_MODAL_MODES.ADD}
            />
          </div>
        </div>

        {/* Right Side — Selected Materials */}
        <div className="px-2 py-6">
          <h2 className="text-xl text-center font-semibold text-gray-800 mb-4">
            Selected Materials
          </h2>

          {materials.length === 0 ? (
            <div className="text-gray-600 text-center py-11 border rounded-lg">
              No materials selected yet.
            </div>
          ) : (
            <div className="max-h-[40vh] overflow-y-auto">
                <GenericTable
                  tableColumns={
                    mode === STOCK_MODAL_MODES.VIEW
                      ? DISPATCH_NOTE_MATERIAL_COLUMN.filter(
                          (col) => col !== "actions" && col !== "available_quantity"
                        )
                      : DISPATCH_NOTE_MATERIAL_COLUMN
                  }
                  tableRows={materials}
                  columnLabels={DISPATCH_NOTE_MATERIAL_LABEL}
                  renderField={renderField}
                  enableMobileCardView={false}
                  className={""}
                />
            </div>
          )}

          {/* Submit Button */}
          <div className="mt-3">
            {/* INVENTORY_INCHARGE — Draft or Add */}
            {mode !== STOCK_MODAL_MODES.VIEW && (
              <div className="flex justify-end gap-4 mt-6">
                <RoleValidator allowedRoles={[ROLES.INVENTORY_INCHARGE]}>
                  <button
                    disabled={hasError}
                    onClick={() => handleSubmit(DISPATCH_NOTE_STATUS.DRAFT)}
                    className={`px-4 text-white font-medium py-2 rounded-lg transition-all my-2 ${
                      hasError
                        ? "bg-gray-400"
                        : "bg-gray-200 text-gray-800 hover:bg-gray-300"
                    }`}
                  >
                    Save as Draft
                  </button>
                </RoleValidator>

                <RoleValidator allowedRoles={[ROLES.INVENTORY_INCHARGE]}>
                  <button
                    disabled={hasError}
                    onClick={() =>
                      handleSubmit(DISPATCH_NOTE_STATUS.PENDING_FOR_APPROVAL)
                    }
                    className={`px-4 text-white font-medium py-2 rounded-lg transition-all my-2 ${
                      hasError ? "bg-gray-400" : "bg-primary hover:bg-blue-700"
                    }`}
                  >
                    Submit For Approval
                  </button>
                </RoleValidator>
              </div>
            )}

            {/* INVENTORY_ADMIN — Approve / Reject */}
            {mode === STOCK_MODAL_MODES.VIEW &&
              status === DISPATCH_NOTE_STATUS.PENDING_FOR_APPROVAL && (
                <div className="flex justify-end gap-4 mt-6">
                  <RoleValidator allowedRoles={[ROLES.INVENTORY_ADMIN]}>
                    <button
                      onClick={() =>
                        handleSubmit(DISPATCH_NOTE_STATUS.APPROVED)
                      }
                      className="px-4 bg-green-600 hover:bg-green-700 text-white py-2 rounded-lg"
                    >
                      Approve
                    </button>
                  </RoleValidator>
                  <RoleValidator allowedRoles={[ROLES.INVENTORY_ADMIN]}>
                    <button
                      onClick={() =>
                        handleSubmit(DISPATCH_NOTE_STATUS.REJECTED)
                      }
                      className="px-4 bg-red-500 hover:bg-red-600 text-white py-2 rounded-lg"
                    >
                      Reject
                    </button>
                  </RoleValidator>
                </div>
              )}

            {/* INVENTORY_ADMIN & INVENTORY_INCHARGE — Post Approval Transit Flow */}
            {mode === STOCK_MODAL_MODES.VIEW &&
              status === DISPATCH_NOTE_STATUS.APPROVED && (
                <div className="flex justify-end gap-4 mt-6">
                  <RoleValidator
                    allowedRoles={[
                      ROLES.INVENTORY_INCHARGE,
                    ]}
                  >
                    <button
                      onClick={() =>
                        handleSubmit(DISPATCH_NOTE_STATUS.IN_TRANSIT)
                      }
                      className="px-4 bg-primary hover:bg-primary text-white py-2 rounded-lg"
                    >
                      Mark as In Transit
                    </button>
                  </RoleValidator>
                </div>
              )}

            {mode === STOCK_MODAL_MODES.VIEW &&
              status === DISPATCH_NOTE_STATUS.IN_TRANSIT && (
                <div className="flex justify-end gap-4 mt-6">
                  <RoleValidator
                    allowedRoles={[
                      ROLES.INVENTORY_INCHARGE,
                    ]}
                  >
                    <button
                      onClick={() =>
                        handleSubmit(DISPATCH_NOTE_STATUS.TRANSIT_COMPLETED)
                      }
                      className="px-4 bg-cyan-600 hover:bg-cyan-700 text-white py-2 rounded-lg"
                    >
                      Transit Completed
                    </button>
                  </RoleValidator>
                  <RoleValidator
                    allowedRoles={[
                      ROLES.INVENTORY_INCHARGE,
                    ]}
                  >
                    <button
                      onClick={() =>
                        handleSubmit(DISPATCH_NOTE_STATUS.TRANSIT_FAILED)
                      }
                      className="px-4 bg-orange-500 hover:bg-orange-600 text-white py-2 rounded-lg"
                    >
                      Transit Failed
                    </button>
                  </RoleValidator>
                </div>
              )}
          </div>
        </div>
      </div>
    </div>
  );
}
